import { hasBlueYonderQrCode } from "@redotech/redo-model/integration/blueyonder/utils";
import { hasReturnBearQrCode } from "@redotech/redo-model/integration/returnbear/returnbear-utils";
import {
  getReturner,
  MerchantAppReturn,
  Return,
} from "@redotech/redo-model/return";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useState } from "react";
import { useResendLabel } from "../hooks/use-resend-label";

interface IResendModalProps {
  return_: Return | MerchantAppReturn;
  isOpen: boolean;
  onClose: () => void;
}

export const ResendModal = memo(function ResendModal({
  return_,
  isOpen,
  onClose,
}: IResendModalProps) {
  const initialEmail = getReturner(return_).email || "";
  const [email, setEmail] = useState(initialEmail);

  const resendLabelMut = useResendLabel(return_);
  const errorMessage =
    resendLabelMut.isError && resendLabelMut.error instanceof Error
      ? resendLabelMut.error.message
      : null;

  const hasNormalShippingLabel = !!return_.shipment;
  const isQrCode = hasBlueYonderQrCode(return_) || hasReturnBearQrCode(return_);

  const isFedexEasyReturnsFailure =
    return_.blueYonder &&
    return_.blueYonder.returns?.length === 0 &&
    !hasNormalShippingLabel;

  const handleResendLabel = useCallback(() => {
    resendLabelMut.mutate({ emailOverride: email }, { onSuccess: onClose });
  }, [email, resendLabelMut, onClose]);

  const { title, subtitle, showWarning } = useMemo(() => {
    if (isFedexEasyReturnsFailure) {
      return {
        title: "Send Shipping Label",
        subtitle:
          "This Return was supposed to create a Fedex Easy Returns QR code, but failed to do so. Please review the activity timeline for how to continue as a Fedex Easy Return.",
        showWarning: true,
      };
    }

    if (isQrCode) {
      return {
        title: "Resend QR code",
        subtitle: "This will send an email with the QR code to the customer.",
        showWarning: false,
      };
    }

    return {
      title: "Resend Shipping Label",
      subtitle:
        "This will send an email with the shipping label(s) to the customer.",
      showWarning: false,
    };
  }, [isFedexEasyReturnsFailure, isQrCode]);
  const isDisabled = resendLabelMut.isPending || !email.trim();

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Send",
        onClick: handleResendLabel,
        disabled: isDisabled,
        pending: resendLabelMut.isPending,
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: onClose,
        disabled: resendLabelMut.isPending,
      }}
      subtitle={subtitle}
      title={title}
    >
      <Flex dir="column" gap="2xl">
        <RedoTextInput
          label="Email address"
          placeholder="Enter email address"
          setValue={setEmail}
          value={email}
        />

        {showWarning && (
          <Text fontSize="sm" fontWeight="thin" textColor="error">
            Sending this will generate a new shipping label for the customer to
            package themselves.
          </Text>
        )}

        {errorMessage && (
          <Text fontSize="sm" fontWeight="thin" textColor="error">
            {errorMessage}
          </Text>
        )}
      </Flex>
    </RedoModal>
  );
});
